import { stepProgress } from '../../warehouse/fulfilmentGuide';

// The Sales Order header stepper's steps (SalesOrderStepper.js, issue
// #310), worked out fresh from the order as it is now. Pick, Pack and Ship
// count SKUs fully through each stage out of the order's SKUs, through the
// same stepProgress the Pick, Pack and Ship screen uses, so quantities
// cancelled off a backorder are left out and the two always agree.

const PAST = { pick: 'picked', pack: 'packed', ship: 'shipped' };

// Only Draft, Submitted and Accepted need explaining; Pick, Pack and Ship's
// own count says it all.
const MEANING = {
  draft: "Indicates if the order has been created by staff, or is in the Customer's portal cart.",
  submitted: 'Indicates if the order has been submitted by staff, placed by the Customer from their cart, or converted from an accepted Quote.',
  accepted: "Indicates if the order has been accepted by staff, via the order's Actions menu.",
};

// Submitted is never the step the order is at: Submit moves it straight
// from Draft to waiting at Accepted.
const NEXT = {
  draft: 'Next: Submit.',
  accepted: 'Next: Accept Order.',
  pick: 'Next: Pick.',
  pack: 'Next: Pack.',
  ship: 'Next: Ship.',
};

const HOLD_NEXT = 'On Hold: release the hold first.';

// The header's Next button label for the step the order is at.
const ACTION = {
  draft: 'Submit',
  accepted: 'Accept Order',
  pick: 'Pick',
  pack: 'Pack',
  ship: 'Ship',
};

function fulfilmentText(step) {
  const past = PAST[step.key];
  if (step.total === 0) return `No SKUs to be ${past}`;
  if (step.done >= step.total) return `All ${step.total} SKU${step.total === 1 ? '' : 's'} ${past}`;
  return `${step.done} of ${step.total} SKUs ${past}, ${step.total - step.done} remaining`;
}

// The stepper worked out from the order: { steps, current, hold,
// holdReason, complete, next, nextKey, nextLabel }. `order` is { status,
// on_hold, on_hold_reason, lines } with each line's qty, qty_cancelled,
// qty_picked, qty_packed and qty_shipped. `current` is the index of the
// step the order is at (null once it's complete); `next` is what to do
// about it. `nextKey`/`nextLabel` are the header Next button's: the step's
// key ('hold' when On Hold) and its label ("Accept Order").
export function salesOrderSteps(order) {
  const status = order?.status || 'draft';
  const draft = status === 'draft';
  const accepted = !['draft', 'submitted'].includes(status);
  const fulfilment = stepProgress({ lines: order?.lines || [] }).map((step) => (accepted
    ? step
    : { ...step, done: 0, state: 'empty' }));
  const steps = [
    { key: 'draft', label: 'Draft', state: draft ? 'empty' : 'full', progress: draft ? 'Not submitted yet' : 'Done' },
    { key: 'submitted', label: 'Submitted', state: draft ? 'empty' : 'full', progress: draft ? 'Not submitted yet' : 'Done' },
    { key: 'accepted', label: 'Accepted', state: accepted ? 'full' : 'empty', progress: accepted ? 'Done' : 'Not accepted yet' },
    ...fulfilment.map((step) => ({ ...step, progress: fulfilmentText(step) })),
  ].map((step) => ({ ...step, meaning: MEANING[step.key] }));

  const complete = status === 'shipped' || (accepted && fulfilment.every((s) => s.state === 'full'));
  let current = null;
  if (complete) {
    steps.forEach((s) => { s.state = 'full'; });
  } else if (draft) {
    current = 0;
  } else if (!accepted) {
    current = 2;
  } else {
    current = 3 + Math.max(0, fulfilment.findIndex((s) => s.state !== 'full'));
  }
  const hold = !!order?.on_hold;
  const next = current === null ? null : hold ? HOLD_NEXT : NEXT[steps[current].key];
  const nextKey = current === null ? null : hold ? 'hold' : steps[current].key;
  const nextLabel = nextKey === 'hold' ? 'Release Hold' : ACTION[nextKey] || null;
  return { steps, current, hold, holdReason: order?.on_hold_reason || '', complete, next, nextKey, nextLabel };
}
